import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Company, CreateCompanyRequest, UpdateCompanyRequest, PriceEntryMode } from '../../types';
import { DEFAULT_VAT_RATE, isSupportedVatRate, VAT_RATE_OPTIONS } from '../../constants/vat';
import { useLanguage } from '../../contexts/LanguageContext';
import PhoneInput from '../common/PhoneInput';
import { errorMessage } from '../../utils/errorMessage';
import { AppDialogFormActions } from '../common/AppDialog';

interface CompanyFormProps {
  company?: Company | null;
  onSubmit: (data: CreateCompanyRequest | UpdateCompanyRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const CompanyForm: React.FC<CompanyFormProps> = ({
  company,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    priceEntryMode: PriceEntryMode.VAT_EXCLUDED,
    defaultVatRate: String(DEFAULT_VAT_RATE),
    batchTrackingEnabled: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        email: company.email || '',
        phone: company.phone || '',
        address: company.address || '',
        website: company.website || '',
        priceEntryMode: company.priceEntryMode || PriceEntryMode.VAT_EXCLUDED,
        defaultVatRate: String(company.defaultVatRate ?? DEFAULT_VAT_RATE),
        batchTrackingEnabled: company.batchTrackingEnabled ?? false,
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        website: '',
        priceEntryMode: PriceEntryMode.VAT_EXCLUDED,
        defaultVatRate: String(DEFAULT_VAT_RATE),
        batchTrackingEnabled: false,
      });
    }
    setErrors({});
    setSubmitError(null);
  }, [company]);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Name is required
    if (!formData.name.trim()) {
      newErrors.name = t('companies.nameRequired');
    }

    // Email format validation (if provided)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('companies.emailInvalid');
    }

    // Phone format validation (if provided) - simple validation
    if (formData.phone && !/^[+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/[\s\-()]/g, ''))) {
      newErrors.phone = t('companies.phoneInvalid');
    }

    // Website format validation (if provided)
    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = t('companies.websiteInvalid');
    }

    const defaultVatRate = Number(formData.defaultVatRate);
    if (!isSupportedVatRate(defaultVatRate)) {
      newErrors.defaultVatRate = t('companies.defaultVatRateInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare data (remove empty strings)
      const submitData = {
        ...Object.fromEntries(
          Object.entries(formData).filter(([key, value]) => (
            key === 'defaultVatRate'
            || key === 'batchTrackingEnabled'
            || typeof value !== 'string'
            || value.trim() !== ''
          ))
        ),
        defaultVatRate: Number(formData.defaultVatRate),
      };

      await onSubmit(submitData);
    } catch (error: unknown) {
      setSubmitError(errorMessage(error, t('companies.saveError')));
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submitError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, flex: 1 }}>
        <Typography variant="body2" color="text.secondary">{t('companies.formHint')}</Typography>

        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2
          }}
        >
          <TextField
            fullWidth
            label={t('companies.companyName')}
            name="name"
            value={formData.name}
            onChange={handleInputChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            required
            disabled={isLoading}
          />

          <TextField
            fullWidth
            label={t('common.email')}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            error={!!errors.email}
            helperText={errors.email}
            disabled={isLoading}
          />

          <PhoneInput
            value={formData.phone}
            onChange={(value) => {
              setFormData(prev => ({ ...prev, phone: value }));
              if (errors.phone) {
                setErrors(prev => ({ ...prev, phone: '' }));
              }
            }}
            label={t('common.phone')}
            error={!!errors.phone}
            helperText={errors.phone}
            disabled={isLoading}
            fullWidth
          />

          <TextField
            fullWidth
            label={t('companies.website')}
            name="website"
            type="url"
            value={formData.website}
            onChange={handleInputChange('website')}
            error={!!errors.website}
            helperText={errors.website}
            placeholder="https://www.example.com"
            disabled={isLoading}
          />

          <TextField
            fullWidth
            label={t('common.address')}
            name="address"
            multiline
            rows={3}
            value={formData.address}
            onChange={handleInputChange('address')}
            error={!!errors.address}
            helperText={errors.address}
            disabled={isLoading}
            sx={{ gridColumn: '1 / -1' }}
          />

          <FormControl fullWidth disabled={isLoading} sx={{ gridColumn: '1 / -1' }}>
            <InputLabel>{t('companies.priceEntryMode')}</InputLabel>
            <Select
              value={formData.priceEntryMode}
              label={t('companies.priceEntryMode')}
              onChange={(event) => setFormData(prev => ({
                ...prev,
                priceEntryMode: event.target.value as PriceEntryMode,
              }))}
            >
              <MenuItem value={PriceEntryMode.VAT_EXCLUDED}>{t('companies.priceEntryModeVatExcluded')}</MenuItem>
              <MenuItem value={PriceEntryMode.VAT_INCLUDED}>{t('companies.priceEntryModeVatIncluded')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth error={!!errors.defaultVatRate} disabled={isLoading}>
            <InputLabel id="company-default-vat-rate-label">{t('companies.defaultVatRate')}</InputLabel>
            <Select
              labelId="company-default-vat-rate-label"
              value={formData.defaultVatRate}
              label={t('companies.defaultVatRate')}
              onChange={(event) => {
                setFormData(prev => ({ ...prev, defaultVatRate: event.target.value }));
                if (errors.defaultVatRate) {
                  setErrors(prev => ({ ...prev, defaultVatRate: '' }));
                }
              }}
            >
              {VAT_RATE_OPTIONS.map(rate => (
                <MenuItem key={rate} value={String(rate)}>%{rate}</MenuItem>
              ))}
            </Select>
            <FormHelperText>{errors.defaultVatRate || t('companies.defaultVatRateHint')}</FormHelperText>
          </FormControl>

          <Box sx={{ gridColumn: '1 / -1', p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={formData.batchTrackingEnabled}
                  onChange={(event) => setFormData(prev => ({
                    ...prev,
                    batchTrackingEnabled: event.target.checked,
                  }))}
                  disabled={isLoading}
                />
              )}
              label={t('companies.batchTrackingEnabled')}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
              {t('companies.batchTrackingHint')}
            </Typography>
          </Box>
        </Box>

        <AppDialogFormActions>
          <Button
            variant="outlined"
            sx={{ minHeight: 44 }}
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{ minHeight: 44 }}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? t('companies.saving') : (company ? t('companies.update') : t('companies.create'))}
          </Button>
        </AppDialogFormActions>
      </Box>
    </Box>
  );
};

export default CompanyForm;
